/**
 * Générateur — Automatisme : dans une configuration donnée, déterminer les
 * images de figures, de droites, de segments, de points par une symétrie
 * axiale ou un demi-tour.
 * Attendu ID : 1121 | Niveau : 4ème | Domaine : Transformations
 * Rubrique : AUTOMATISME (programme 2026, Parallélogrammes et translations 4e).
 * Labels (cartes exerciseur) :
 *   - L'image d'un point                       (QCM sur figure codée)
 *   - L'image d'un segment ou d'une droite     (QCM sur figure codée)
 *   - L'image d'une figure                     (QCM sur figure codée : triangles)
 *   - Points et images                         (relier point ↔ image)
 *   - Vrai ou faux ?                           (V/F sur figure)
 *   - Images : vrai ou faux ?                  (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-09) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Parallélogrammes et translations 4e + grille
 * P1-P12. P1 : CM2 introduit (figure symétrique sur quadrillage), 6e
 * consolidé (symétrique d'un point, propriétés), 5e étendu (demi-tour), 4e
 * automatisme en lecture. P2 : sens installés — pliage / miroir, tête en bas,
 * centre milieu ; non installés (distracteurs) — l'image d'une droite ou d'un
 * segment comme objet, la correspondance sommet par sommet, la confusion
 * entre l'image par l'axe et l'image par le centre. P3 : aucun nombre,
 * figures usuelles codées. P5 : feuille blanche, axe en pointillés, centre
 * marqué. P6 : lecture seule. P7 : translation non nommée, « demi-tour »
 * (mot du BO). P8 : image, axe, centre, [AB], (AB). P9 : symétrique d'un point
 * (5e), demi-tour d'un point (1119), reconnaître la transformation (1120).
 * P10 : constat. P11 : point de l'axe et centre sont leur propre image ;
 * l'image d'une droite par un demi-tour est parallèle ; on nomme l'image de
 * [AB] avec les images de A et de B. P12 : rémanence forte.
 * CONFIGURATIONS : rectangle ABCD (centre O, milieux I, J), carré ABCD (centre
 * O, diagonales), hexagone régulier ABCDEF (centre O), losange ABCD (centre O,
 * diagonales), deux triangles ABC et DEF symétriques (axe (d) avec un point M,
 * ou centre O), triangle ABC isocèle en A avec H milieu de [BC] (axe (AH)).
 * INVARIANTS :
 *   - toute image est CALCULÉE (réflexion ou demi-tour) puis retrouvée parmi
 *     les points nommés : chaque configuration est fermée par ses transformations ;
 *   - segments, droites et triangles sont nommés par lettres triées ([CD], jamais
 *     [DC] en double) ;
 *   - la figure porte data-points / data-transfo / data-centre / data-axe pour les solveurs ;
 *   - V/F : pas de crochets, mixité 1-3 faux, familles exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeRelier,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 1121;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_POINT = 'L\'image d\'un point';
const L_SEGMENT = 'L\'image d\'un segment ou d\'une droite';
const L_FIGURE = 'L\'image d\'une figure';
const L_RELIER = 'Points et images';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Images : vrai ou faux ?';

// ─── Géométrie ─────────────────────────────────────────────────────────────
const EPS = 1e-6;
const egal = (p, q) => Math.abs(p[0] - q[0]) < EPS && Math.abs(p[1] - q[1]) < EPS;
const reflechir = (p, a, b) => {
    const dx = b[0] - a[0]; const dy = b[1] - a[1];
    const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy);
    const h = [a[0] + t * dx, a[1] + t * dy];
    return [2 * h[0] - p[0], 2 * h[1] - p[1]];
};
const demiTour = (p, o) => [2 * o[0] - p[0], 2 * o[1] - p[1]];
const r2 = (v) => Math.round(v * 100) / 100;

/** Coordonnées de l'image d'un point par une transformation de la configuration. */
const imageCoords = (cfg, T, p) => (T.type === 'demitour' ? demiTour(p, cfg.points[T.centre]) : reflechir(p, T.axe[0], T.axe[1]));
/** Étiquette de l'image d'un point nommé (null si l'image n'est pas un point nommé). */
const imageDe = (cfg, T, label) => {
    const q = imageCoords(cfg, T, cfg.points[label]);
    return Object.keys(cfg.points).find((k) => egal(cfg.points[k], q)) || null;
};
const nomSeg = (a, b) => `[${[a, b].sort().join('')}]`;
const nomDroite = (a, b) => `(${[a, b].sort().join('')})`;
const nomTri = (t) => `triangle ${[...t].sort().join('')}`;
const imageSeg = (cfg, T, [a, b]) => nomSeg(imageDe(cfg, T, a), imageDe(cfg, T, b));
const imageDroite = (cfg, T, [a, b]) => nomDroite(imageDe(cfg, T, a), imageDe(cfg, T, b));
const imageTri = (cfg, T, t) => nomTri(t.map((s) => imageDe(cfg, T, s)));
const pointsFixes = (cfg, T) => Object.keys(cfg.points).filter((k) => imageDe(cfg, T, k) === k);

// ─── Configurations ────────────────────────────────────────────────────────
const axeParPoints = (cfg, a, b) => [cfg.points[a], cfg.points[b]];

const configRectangle = () => {
    const cfg = {
        id: 'rectangle',
        intro: 'ABCD est un rectangle de centre O. I est le milieu de [AB] et J le milieu de [DC].',
        points: { A: [-4, 2], B: [4, 2], C: [4, -2], D: [-4, -2], O: [0, 0], I: [0, 2], J: [0, -2] },
        cotes: [['A', 'B'], ['B', 'C'], ['C', 'D'], ['D', 'A']],
        rayons: [['A', 'O'], ['B', 'O'], ['C', 'O'], ['D', 'O']],
        triangles: [['A', 'B', 'O'], ['B', 'C', 'O'], ['C', 'D', 'O'], ['D', 'A', 'O']],
        codage: 'rectangle',
    };
    cfg.transfos = [
        { id: 'O', type: 'demitour', centre: 'O', nom: 'le demi-tour de centre O' },
        { id: 'IJ', type: 'axiale', axe: axeParPoints(cfg, 'I', 'J'), nom: 'la symétrie d\'axe (IJ)' },
    ];
    return cfg;
};

const configCarre = () => {
    const cfg = {
        id: 'carre',
        intro: 'ABCD est un carré de centre O.',
        points: { A: [-3, 3], B: [3, 3], C: [3, -3], D: [-3, -3], O: [0, 0] },
        cotes: [['A', 'B'], ['B', 'C'], ['C', 'D'], ['D', 'A']],
        rayons: [['A', 'O'], ['B', 'O'], ['C', 'O'], ['D', 'O']],
        triangles: [['A', 'B', 'O'], ['B', 'C', 'O'], ['C', 'D', 'O'], ['D', 'A', 'O']],
        codage: 'carre',
    };
    cfg.transfos = [
        { id: 'O', type: 'demitour', centre: 'O', nom: 'le demi-tour de centre O' },
        { id: 'AC', type: 'axiale', axe: axeParPoints(cfg, 'A', 'C'), nom: 'la symétrie d\'axe (AC)' },
        { id: 'BD', type: 'axiale', axe: axeParPoints(cfg, 'B', 'D'), nom: 'la symétrie d\'axe (BD)' },
    ];
    return cfg;
};

const configHexagone = () => {
    const R = 3.4; const lettres = ['A', 'B', 'C', 'D', 'E', 'F']; const points = { O: [0, 0] };
    lettres.forEach((l, i) => { const a = (Math.PI / 2) - i * (Math.PI / 3); points[l] = [R * Math.cos(a), R * Math.sin(a)]; });
    const cfg = {
        id: 'hexagone',
        intro: 'ABCDEF est un hexagone régulier de centre O.',
        points,
        cotes: lettres.map((l, i) => [l, lettres[(i + 1) % 6]]),
        rayons: lettres.map((l) => [l, 'O']),
        triangles: lettres.map((l, i) => [l, lettres[(i + 1) % 6], 'O']),
        codage: 'hexagone',
    };
    cfg.transfos = [
        { id: 'O', type: 'demitour', centre: 'O', nom: 'le demi-tour de centre O' },
        { id: 'AD', type: 'axiale', axe: axeParPoints(cfg, 'A', 'D'), nom: 'la symétrie d\'axe (AD)' },
        { id: 'BE', type: 'axiale', axe: axeParPoints(cfg, 'B', 'E'), nom: 'la symétrie d\'axe (BE)' },
        { id: 'CF', type: 'axiale', axe: axeParPoints(cfg, 'C', 'F'), nom: 'la symétrie d\'axe (CF)' },
    ];
    return cfg;
};

/** Deux triangles ABC et DEF symétriques par rapport à une droite (d) ou à un point O. */
const configTriangles = (type = randomChoice(['axiale', 'demitour'])) => {
    const base = [[-4, 1.5], [-1.5, 3], [-2.5, -1.5]];
    const flip = Math.random() < 0.5; const horizontal = Math.random() < 0.5;
    const place = ([x, y]) => { const p = flip ? [-x, y] : [x, y]; return horizontal ? [p[1], p[0]] : p; };
    const ABC = base.map(place);
    const axe = horizontal ? [[-1, 0], [1, 0]] : [[0, -1], [0, 1]];
    const img = ABC.map((p) => (type === 'demitour' ? demiTour(p, [0, 0]) : reflechir(p, axe[0], axe[1])));
    const noms = shuffle(['D', 'E', 'F']);
    const points = { A: ABC[0], B: ABC[1], C: ABC[2] };
    noms.forEach((n, i) => { points[n] = img[i]; });
    const t = randomChoice([-4, 4]);
    if (type === 'demitour') points.O = [0, 0];
    else points.M = horizontal ? [t, 0] : [0, t];
    const cfg = {
        id: type === 'demitour' ? 'triangles_o' : 'triangles_d',
        intro: type === 'demitour'
            ? 'Les triangles ABC et DEF sont symétriques par rapport au point O.'
            : 'Les triangles ABC et DEF sont symétriques par rapport à la droite (d). M est un point de (d).',
        points,
        cotes: [['A', 'B'], ['B', 'C'], ['C', 'A'], ['D', 'E'], ['E', 'F'], ['F', 'D']],
        rayons: [],
        triangles: [['A', 'B', 'C'], ['D', 'E', 'F']],
        codage: 'triangles',
        horizontal,
    };
    cfg.transfos = type === 'demitour'
        ? [{ id: 'O', type: 'demitour', centre: 'O', nom: 'le demi-tour de centre O' }]
        : [{ id: 'd', type: 'axiale', axe, axeNom: '(d)', nom: 'la symétrie d\'axe (d)' }];
    return cfg;
};

/** Losange ABCD de centre O (diagonales tracées) : demi-tour, symétries d'axes (AC) et (BD). Ajouté en revue (2026-09-10). */
const configLosange = () => {
    const cfg = {
        id: 'losange',
        intro: 'ABCD est un losange de centre O.',
        points: { A: [0, 3], B: [4, 0], C: [0, -3], D: [-4, 0], O: [0, 0] },
        cotes: [['A', 'B'], ['B', 'C'], ['C', 'D'], ['D', 'A']],
        rayons: [['A', 'O'], ['B', 'O'], ['C', 'O'], ['D', 'O']],
        triangles: [['A', 'B', 'O'], ['B', 'C', 'O'], ['C', 'D', 'O'], ['D', 'A', 'O']],
        codage: 'losange',
    };
    cfg.transfos = [
        { id: 'O', type: 'demitour', centre: 'O', nom: 'le demi-tour de centre O' },
        { id: 'AC', type: 'axiale', axe: axeParPoints(cfg, 'A', 'C'), nom: 'la symétrie d\'axe (AC)' },
        { id: 'BD', type: 'axiale', axe: axeParPoints(cfg, 'B', 'D'), nom: 'la symétrie d\'axe (BD)' },
    ];
    return cfg;
};

/** Triangle ABC isocèle en A, H milieu de [BC] : symétrie d'axe (AH) seulement. Ajouté en revue (2026-09-10). */
const configIsocele = () => {
    const cfg = {
        id: 'isocele',
        intro: 'ABC est un triangle isocèle en A. H est le milieu de [BC].',
        points: { A: [0, 3.2], B: [-3, -2], C: [3, -2], H: [0, -2] },
        cotes: [['A', 'B'], ['B', 'C'], ['C', 'A']],
        rayons: [['A', 'H'], ['B', 'H'], ['C', 'H']],
        triangles: [['A', 'B', 'H'], ['A', 'C', 'H']],
        codage: 'isocele',
        droites: [['A', 'H']],                                   // quatrième droite nommable (revue 2026-09-10)
    };
    cfg.transfos = [{ id: 'AH', type: 'axiale', axe: axeParPoints(cfg, 'A', 'H'), nom: 'la symétrie d\'axe (AH)' }];
    return cfg;
};

const CONFIGS = [configRectangle, configCarre, configHexagone, configLosange, configTriangles, configIsocele];
const CONFIGS_FIGURES = [configRectangle, configCarre, configHexagone, configLosange];      // au moins quatre triangles nommés
const tirerConfig = (avecFigures = false) => randomChoice(avecFigures ? CONFIGS_FIGURES : CONFIGS)();
/** Transformation : type tiré à pile ou face quand la configuration en a des deux sortes. */
const tirerTransfo = (cfg) => {
    const types = [...new Set(cfg.transfos.map((T) => T.type))];
    const type = randomChoice(types);
    return randomChoice(cfg.transfos.filter((T) => T.type === type));
};
const autresImages = (cfg, T, calc) => cfg.transfos.filter((U) => U !== T).map((U) => calc(U));

// ─── Codes d'erreur (contenu.erreurs) ──────────────────────────────────────
/** Le code d'une image fausse f de `soi` par T : le départ redonné, ou l'image par une autre symétrie (`image(U)`) ; null sinon ou si deux mènent à f. */
const codeImage = (cfg, T, soi, f, image) => {
    const c = new Set();
    if (f === soi) c.add('depart_pris_pour_image');
    cfg.transfos.forEach((U) => { if (U !== T && image(U) === f) c.add(U.type === T.type ? 'autre_axe_de_la_figure' : 'demi_tour_pour_axiale'); });
    return c.size === 1 ? [...c][0] : null;
};

// ─── Figure SVG (feuille blanche, codages) ─────────────────────────────────
const W = 300; const H = 260; const CX = 150; const CY = 130;
const ECHELLE = { rectangle: 30, carre: 32, hexagone: 30, losange: 30, triangles: 27, isocele: 30 };
let U = 27; // échelle courante (unités → pixels), fixée par dessiner() selon la configuration
const px = (x) => CX + x * U; const py = (y) => CY - y * U;
const P = (p) => `${px(p[0]).toFixed(1)},${py(p[1]).toFixed(1)}`;
const FONT = 'font-family="system-ui,sans-serif" font-weight="700"';
const HALO = 'paint-order="stroke" stroke="#ffffff" stroke-width="3"';
const texte = (x, y, s, couleur = '#1e293b', anchor = 'middle', taille = 14) => `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" ${FONT} font-size="${taille}" fill="${couleur}" text-anchor="${anchor}" ${HALO}>${s}</text>`;
const unit = (v) => { const n = Math.hypot(v[0], v[1]) || 1; return [v[0] / n, v[1] / n]; };
const angleDroit = (p, q, r) => {
    const u = unit([q[0] - p[0], q[1] - p[1]]); const v = unit([r[0] - p[0], r[1] - p[1]]); const s = 0.35;
    const a = [p[0] + u[0] * s, p[1] + u[1] * s]; const b = [a[0] + v[0] * s, a[1] + v[1] * s]; const c = [p[0] + v[0] * s, p[1] + v[1] * s];
    return `<polyline points="${P(a)} ${P(b)} ${P(c)}" fill="none" stroke="#1e293b" stroke-width="1.2"/>`;
};
const tick = (a, b, n2 = 1) => {
    const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]; const n = unit([-(b[1] - a[1]), b[0] - a[0]]); const u = unit([b[0] - a[0], b[1] - a[1]]); const s = 0.18;
    let g = '';
    for (let i = 0; i < n2; i++) {
        const k = (i - (n2 - 1) / 2) * 0.16; const c = [m[0] + u[0] * k, m[1] + u[1] * k];
        g += `<line x1="${px(c[0] + n[0] * s)}" y1="${py(c[1] + n[1] * s)}" x2="${px(c[0] - n[0] * s)}" y2="${py(c[1] - n[1] * s)}" stroke="#1e293b" stroke-width="1.6"/>`;
    }
    return g;
};
const seg = (a, b, couleur = '#1e293b', largeur = 1.8, dash = '') => `<line x1="${px(a[0])}" y1="${py(a[1])}" x2="${px(b[0])}" y2="${py(b[1])}" stroke="${couleur}" stroke-width="${largeur}"${dash ? ` stroke-dasharray="${dash}"` : ''}/>`;

/** Position de l'étiquette d'un point : vers l'extérieur depuis un centre de référence. */
const etiquette = (label, p, ref) => {
    const d = unit([p[0] - ref[0], p[1] - ref[1]]);
    return texte(px(p[0]) + d[0] * 15, py(p[1]) - d[1] * 15 + 5, label);
};

const dessiner = (cfg, T, extra = '') => {
    U = ECHELLE[cfg.codage];
    let g = `<rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>`;
    const pts = cfg.points;
    if (cfg.codage === 'triangles') {
        const tri = (t, fill) => `<polygon points="${t.map((l) => P(pts[l])).join(' ')}" fill="${fill}" fill-opacity="0.35" stroke="${fill}" stroke-width="1.8" stroke-linejoin="round"/>`;
        g += tri(['A', 'B', 'C'], '#1e3a8a') + tri(['D', 'E', 'F'], '#b45309');
    } else {
        const contour = Object.keys(pts).filter((k) => /^[A-F]$/.test(k));
        g += `<polygon points="${contour.map((l) => P(pts[l])).join(' ')}" fill="#dbeafe" fill-opacity="0.6" stroke="#1e293b" stroke-width="1.8" stroke-linejoin="round"/>`;
        // diagonales / rayons en trait fin
        cfg.rayons.forEach(([a, b]) => { g += seg(pts[a], pts[b], '#64748b', 1); });
        if (cfg.codage === 'rectangle' || cfg.codage === 'carre') {
            g += angleDroit(pts.A, pts.B, pts.D) + angleDroit(pts.B, pts.A, pts.C) + angleDroit(pts.C, pts.B, pts.D) + angleDroit(pts.D, pts.A, pts.C);
        }
        if (cfg.codage === 'rectangle') g += tick(pts.A, pts.I) + tick(pts.I, pts.B) + tick(pts.D, pts.J) + tick(pts.J, pts.C);
        if (cfg.codage === 'carre' || cfg.codage === 'losange') cfg.cotes.forEach(([a, b]) => { g += tick(pts[a], pts[b]); });
        if (cfg.codage === 'isocele') g += tick(pts.A, pts.B) + tick(pts.A, pts.C) + tick(pts.B, pts.H, 2) + tick(pts.H, pts.C, 2) + angleDroit(pts.H, pts.C, pts.A);
    }
    // transformation : axe en pointillés prolongé, ou centre marqué
    if (T.type === 'axiale') {
        const [a, b] = T.axe; const d = unit([b[0] - a[0], b[1] - a[1]]);
        const ext = cfg.codage === 'triangles' ? 4.6 : 0.7;
        const a2 = cfg.codage === 'triangles' ? [-d[0] * ext, -d[1] * ext] : [a[0] - d[0] * ext, a[1] - d[1] * ext];
        const b2 = cfg.codage === 'triangles' ? [d[0] * ext, d[1] * ext] : [b[0] + d[0] * ext, b[1] + d[1] * ext];
        g += seg(a2, b2, '#0f766e', 1.8, '6 4');
        if (T.axeNom) {
            // nom de l'axe à l'extrémité opposée au point M
            const m = cfg.points.M; const versB = cfg.horizontal ? m[0] < 0 : m[1] < 0; const e = versB ? b2 : a2;
            const x = cfg.horizontal ? px(e[0]) + (versB ? -2 : 2) : px(e[0]) + 12;
            const y = cfg.horizontal ? py(e[1]) - 8 : py(e[1]) + (versB ? 12 : -6);
            g += texte(x, y, T.axeNom, '#0f766e', cfg.horizontal ? (versB ? 'end' : 'start') : 'start', 12);
        }
    }
    // points
    const centreRef = cfg.codage === 'triangles' ? null : [0, 0];
    const centroide = (t) => [t.reduce((s, l) => s + pts[l][0], 0) / 3, t.reduce((s, l) => s + pts[l][1], 0) / 3];
    const cABC = cfg.codage === 'triangles' ? centroide(['A', 'B', 'C']) : null; const cDEF = cfg.codage === 'triangles' ? centroide(['D', 'E', 'F']) : null;
    Object.keys(pts).forEach((k) => {
        const p = pts[k];
        if (k === 'O') { g += `<circle cx="${px(p[0])}" cy="${py(p[1])}" r="3.5" fill="#b45309"/>` + texte(px(p[0]) + 9, py(p[1]) - 7, 'O', '#b45309', 'start'); return; }
        if (k === 'M') { g += `<circle cx="${px(p[0])}" cy="${py(p[1])}" r="3.5" fill="#0f766e"/>` + texte(px(p[0]) + 9, py(p[1]) + (cfg.horizontal ? -8 : 5), 'M', '#0f766e', 'start'); return; }
        if (k === 'H') { g += `<circle cx="${px(p[0])}" cy="${py(p[1])}" r="2.6" fill="#1e293b"/>` + texte(px(p[0]) + 9, py(p[1]) + 15, 'H'); return; }
        g += `<circle cx="${px(p[0])}" cy="${py(p[1])}" r="2.6" fill="#1e293b"/>`;
        const ref = centreRef || (/[ABC]/.test(k) ? cABC : cDEF);
        g += etiquette(k, p, ref);
    });
    g += extra;
    const data = {
        'data-config': cfg.id,
        'data-points': Object.entries(pts).map(([k, p]) => `${k}:${r2(p[0])};${r2(p[1])}`).join('|'),
        'data-transfo': T.type,
        ...(T.type === 'demitour' ? { 'data-centre': `${r2(pts[T.centre][0])};${r2(pts[T.centre][1])}` } : { 'data-axe': `${r2(T.axe[0][0])};${r2(T.axe[0][1])};${r2(T.axe[1][0])};${r2(T.axe[1][1])}` }),
    };
    const attrs = Object.entries(data).map(([k, v]) => `${k}="${v}"`).join(' ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" ${attrs}>${g}</svg>`;
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refP = { v: null }; const refS = { v: null }; const refF = { v: null }; const refR = { v: null };

/** Complète une liste d'options : bonne + leurres prioritaires (dédoublonnés) + remplissage aléatoire. */
const completer = (bonne, prioritaires, pool, n = 4) => {
    const options = [bonne];
    prioritaires.forEach((o) => { if (o && !options.includes(o)) options.push(o); });
    shuffle(pool).forEach((o) => { if (options.length < n && !options.includes(o)) options.push(o); });
    return options.length === n ? options.sort() : null;
};

// ─── Carte 1 : l'image d'un point ──────────────────────────────────────────

const construirePoint = () => {
    for (let essai = 0; essai < 100; essai++) {
        const cfg = tirerConfig(); const T = tirerTransfo(cfg);
        const fixes = pointsFixes(cfg, T); const labels = Object.keys(cfg.points);
        // point fixe (centre, point de l'axe) 1 fois sur 5
        const p = fixes.length && Math.random() < 0.2 ? randomChoice(fixes) : randomChoice(labels.filter((k) => !fixes.includes(k)));
        const bonne = imageDe(cfg, T, p);
        const options = completer(bonne, shuffle([p, ...autresImages(cfg, T, (U) => imageDe(cfg, U, p))]), labels);
        if (!options) continue;
        const svg = dessiner(cfg, T);
        const payload = makeQcm(options, bonne, `<p>${cfg.intro} Quelle est l'image du point ${p} par ${T.nom} ?</p>`, { ...opts(L_POINT), elements_additionnels: imgElement(svg, 'medium') });
        payload.contenu.choix = options; payload.contenu.bonne_reponse = 'ABCD'[options.indexOf(bonne)];
        payload.contenu = { ...payload.contenu, sousType: T.type };
        payload._meta.params = { config: cfg.id, transfo: T.id, point: p, image: bonne, fixe: bonne === p, signature: `${cfg.id}-${T.id}-${p}` };
        // chaque choix faux construit par une transformation précise (le point de départ, une autre symétrie) ; pas les choix de remplissage
        const erreurs = options.filter((o) => o !== bonne).map((o) => ({ code: codeImage(cfg, T, p, o, (U) => imageDe(cfg, U, p)), valeur: o })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1121 : point sans tirage');
};
const genPoint = () => antiRepeat(construirePoint, refP);

// ─── Carte 2 : l'image d'un segment ou d'une droite ────────────────────────

const construireSegment = () => {
    for (let essai = 0; essai < 100; essai++) {
        const cfg = tirerConfig(); const T = tirerTransfo(cfg);
        const objet = randomChoice(['segment', 'droite']);
        // droites : les côtés, plus les droites déclarées par la configuration (l'axe (AH) de l'isocèle, sinon trois droites seulement)
        const pool = objet === 'segment' ? [...cfg.cotes, ...cfg.rayons] : [...cfg.cotes, ...(cfg.droites || [])];
        const nommer = objet === 'segment' ? nomSeg : nomDroite;
        const calc = objet === 'segment' ? imageSeg : imageDroite;
        const noms = pool.map(([a, b]) => nommer(a, b));
        const s = randomChoice(pool); const soi = nommer(s[0], s[1]);
        const bonne = calc(cfg, T, s);
        if (!noms.includes(bonne)) continue;
        const options = completer(bonne, shuffle([soi, ...autresImages(cfg, T, (U) => calc(cfg, U, s))]), noms);
        if (!options) continue;
        const svg = dessiner(cfg, T);
        const question = objet === 'segment' ? `Quelle est l'image du segment ${soi} par ${T.nom} ?` : `Quelle est l'image de la droite ${soi} par ${T.nom} ?`;
        const payload = makeQcm(options, bonne, `<p>${cfg.intro} ${question}</p>`, { ...opts(L_SEGMENT), elements_additionnels: imgElement(svg, 'medium') });
        payload.contenu.choix = options; payload.contenu.bonne_reponse = 'ABCD'[options.indexOf(bonne)];
        payload.contenu = { ...payload.contenu, sousType: objet };
        payload._meta.params = { config: cfg.id, transfo: T.id, objet, depart: soi, image: bonne, fixe: bonne === soi, signature: `${cfg.id}-${T.id}-${soi}` };
        // chaque choix faux construit par une transformation précise (l'objet de départ, une autre symétrie)
        const erreurs = options.filter((o) => o !== bonne).map((o) => ({ code: codeImage(cfg, T, soi, o, (U) => calc(cfg, U, s)), valeur: o })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1121 : segment sans tirage');
};
const genSegment = () => antiRepeat(construireSegment, refS);

// ─── Carte 3 : l'image d'une figure (triangles) ────────────────────────────

const construireFigure = () => {
    for (let essai = 0; essai < 100; essai++) {
        const cfg = tirerConfig(true); const T = tirerTransfo(cfg);
        const noms = cfg.triangles.map(nomTri);
        const t = randomChoice(cfg.triangles); const soi = nomTri(t);
        const bonne = imageTri(cfg, T, t);
        if (!noms.includes(bonne)) continue;
        const options = completer(bonne, shuffle([soi, ...autresImages(cfg, T, (U) => imageTri(cfg, U, t))]), noms);
        if (!options) continue;
        const svg = dessiner(cfg, T);
        const payload = makeQcm(options, bonne, `<p>${cfg.intro} Quelle est l'image du ${soi} par ${T.nom} ?</p>`, { ...opts(L_FIGURE), elements_additionnels: imgElement(svg, 'medium') });
        payload.contenu.choix = options; payload.contenu.bonne_reponse = 'ABCD'[options.indexOf(bonne)];
        payload.contenu = { ...payload.contenu, sousType: T.type };
        payload._meta.params = { config: cfg.id, transfo: T.id, depart: soi, image: bonne, fixe: bonne === soi, signature: `${cfg.id}-${T.id}-${soi}` };
        // chaque choix faux construit par une transformation précise (le triangle de départ, une autre symétrie)
        const erreurs = options.filter((o) => o !== bonne).map((o) => ({ code: codeImage(cfg, T, soi, o, (U) => imageTri(cfg, U, t)), valeur: o })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1121 : figure sans tirage');
};
const genFigure = () => antiRepeat(construireFigure, refF);

// ─── Carte 4 : points et images (relier) ───────────────────────────────────

const construireRelier = () => {
    const cfg = tirerConfig(); const T = tirerTransfo(cfg);
    const fixes = pointsFixes(cfg, T); const labels = Object.keys(cfg.points);
    const mobiles = shuffle(labels.filter((k) => !fixes.includes(k)));
    // trois points, dont un point fixe une fois sur deux
    // (point fixe imposé si moins de trois points mobiles : l'isocèle n'en a que deux ; revue 2026-09-10)
    const choisis = fixes.length && (Math.random() < 0.5 || mobiles.length < 3) ? [randomChoice(fixes), ...mobiles.slice(0, 2)] : mobiles.slice(0, 3);
    const pairs = choisis.sort().map((p) => ({ source: p, target: imageDe(cfg, T, p) }));
    const cibles = pairs.map((p) => p.target);
    // cibles leurres tirées au hasard : sans code (autre_point rejeté par le validateur, un lien posé au hasard ne se nomme pas)
    const leurres = shuffle(labels.filter((k) => !cibles.includes(k))).slice(0, 2).map((k) => ({ target: k }));
    const svg = dessiner(cfg, T);
    const payload = makeRelier(pairs, `<p>${cfg.intro} Relie chaque point à son image par ${T.nom}.</p>`, { ...opts(L_RELIER), elements_additionnels: imgElement(svg, 'medium'), cibles_leurres: leurres });
    payload.contenu = { ...payload.contenu, sousType: T.type };
    payload._meta.params = { config: cfg.id, transfo: T.id, points: choisis, signature: `${cfg.id}-${T.id}-${choisis.join('')}` };
    return payload;
};
const genRelier = () => antiRepeat(construireRelier, refR);

// ─── Carte 5 : vrai ou faux ? (sur la figure) ──────────────────────────────

let dernieresLignesVf1121 = [];

/** Un leurre d'image pour un objet : lui-même, l'image par une autre transformation, ou un autre objet du pool. */
const leurreImage = (bonne, soi, autres, pool) => {
    const cands = shuffle([soi, ...autres, ...shuffle(pool).slice(0, 3)]).filter((o) => o && o !== bonne);
    return cands.length ? cands[0] : null;
};

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const cfg = tirerConfig(); const T = tirerTransfo(cfg);
        const fixes = pointsFixes(cfg, T); const labels = Object.keys(cfg.points);
        const mobiles = labels.filter((k) => !fixes.includes(k));
        const familles = [];
        // point
        { const p = randomChoice(mobiles); const b = imageDe(cfg, T, p); const f = leurreImage(b, p, autresImages(cfg, T, (U) => imageDe(cfg, U, p)), labels);
            familles.push({ f: 'point', vrai: `L'image du point ${p} par ${T.nom} est le point ${b}.`, faux: f ? `L'image du point ${p} par ${T.nom} est le point ${f}.` : null, code: f ? codeImage(cfg, T, p, f, (U) => imageDe(cfg, U, p)) : null, image: f }); }
        // segment
        { const pool = [...cfg.cotes, ...cfg.rayons]; const s = randomChoice(pool); const soi = nomSeg(s[0], s[1]); const b = imageSeg(cfg, T, s);
            const f = leurreImage(b, soi, autresImages(cfg, T, (U) => imageSeg(cfg, U, s)), pool.map(([x, y]) => nomSeg(x, y)));
            familles.push({ f: 'segment', vrai: `L'image du segment ${soi} par ${T.nom} est le segment ${b}.`, faux: f ? `L'image du segment ${soi} par ${T.nom} est le segment ${f}.` : null, code: f ? codeImage(cfg, T, soi, f, (U) => imageSeg(cfg, U, s)) : null }); }
        // droite
        { const s = randomChoice(cfg.cotes); const soi = nomDroite(s[0], s[1]); const b = imageDroite(cfg, T, s);
            const f = leurreImage(b, soi, autresImages(cfg, T, (U) => imageDroite(cfg, U, s)), cfg.cotes.map(([x, y]) => nomDroite(x, y)));
            familles.push({ f: 'droite', vrai: `L'image de la droite ${soi} par ${T.nom} est la droite ${b}.`, faux: f ? `L'image de la droite ${soi} par ${T.nom} est la droite ${f}.` : null, code: f ? codeImage(cfg, T, soi, f, (U) => imageDroite(cfg, U, s)) : null }); }
        // figure
        { const t = randomChoice(cfg.triangles); const soi = nomTri(t); const b = imageTri(cfg, T, t);
            const f = leurreImage(b, soi, autresImages(cfg, T, (U) => imageTri(cfg, U, t)), cfg.triangles.map(nomTri));
            familles.push({ f: 'figure', vrai: `L'image du ${soi} par ${T.nom} est le ${b}.`, faux: f ? `L'image du ${soi} par ${T.nom} est le ${f}.` : null, code: f ? codeImage(cfg, T, soi, f, (U) => imageTri(cfg, U, t)) : null }); }
        // point fixe
        if (fixes.length) familles.push({ f: 'fixe', vrai: `Le point ${randomChoice(fixes)} est sa propre image par ${T.nom}.`, faux: `Le point ${randomChoice(mobiles)} est sa propre image par ${T.nom}.` });
        const nbFaux = randInt(1, 3);
        const retenues = shuffle(familles).slice(0, 4);
        if (retenues.length !== 4) continue;
        const lignes = shuffle(retenues.map((fa, i) => (i < nbFaux ? { l: fa.faux, v: false, f: fa.f } : { l: fa.vrai, v: true, f: fa.f })));
        if (lignes.some((it) => !it.l)) continue;
        if (essai > 120) dernieresLignesVf1121 = [];
        if (lignes.some((it) => dernieresLignesVf1121.includes(it.l))) continue;
        dernieresLignesVf1121 = lignes.map((it) => it.l);
        const svg = dessiner(cfg, T);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            `<p>${cfg.intro} Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            { ...opts(L_VF), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { config: cfg.id, transfo: T.id, familles: lignes.map((it) => it.f), nbFaux };
        // « Y est sa propre image » (faux) : le point de départ redonné, sauf si une autre symétrie de la figure fixe Y
        const codeFaux = Object.fromEntries(familles.map((fa) => [fa.f, fa.code]));
        const fixe = familles.find((fa) => fa.f === 'fixe');
        if (fixe) { const y = fixe.faux.match(/^Le point ([A-Z]) /)[1]; codeFaux.fixe = codeImage(cfg, T, y, y, (U) => imageDe(cfg, U, y)); }
        // Relecture du 04/10/2026 (juge du 03/10) : un point envoyé sur un point de l'axe (ou sur le centre) ne
        // raconte pas « un autre axe de la figure » : sans nom
        const point = familles.find((fa) => fa.f === 'point');
        if (codeFaux.point === 'autre_axe_de_la_figure' && fixes.includes(point.image)) codeFaux.point = null;
        const erreurs = lignes.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1121 : V/F sans tirage après 160 essais');
};

// ─── Carte 6 : images, vrai ou faux ? (pur rappel) ─────────────────────────

const POOL_RAPPEL = [
    { l: 'Un point situé sur l\'axe d\'une symétrie axiale est sa propre image.', v: true, f: 'axe' },
    { l: 'Un point situé sur l\'axe d\'une symétrie axiale n\'a pas d\'image.', v: false, f: 'axe', code: 'centre_sans_image' },
    { l: 'Le centre d\'un demi-tour est sa propre image.', v: true, f: 'centre' },
    { l: 'L\'image du centre d\'un demi-tour est un autre point.', v: false, f: 'centre', code: 'centre_sans_image' },
    { l: 'L\'image d\'un segment par une symétrie axiale est un segment de même longueur.', v: true, f: 'longueur' },
    { l: 'L\'image d\'un segment par un demi-tour est un segment deux fois plus long.', v: false, f: 'longueur', code: 'conservation_niee' },
    { l: 'Par un demi-tour, l\'image d\'une droite qui ne passe pas par le centre est une droite parallèle.', v: true, f: 'droite' },
    { l: 'Par une symétrie axiale, l\'image d\'une droite est toujours parallèle à la droite de départ.', v: false, f: 'droite', code: 'demi_tour_pour_axiale' },
    { l: 'Une droite qui passe par le centre d\'un demi-tour est sa propre image.', v: true, f: 'invariante' },
    { l: 'L\'image de l\'axe d\'une symétrie axiale est une autre droite.', v: false, f: 'invariante', code: 'centre_sans_image' },
    { l: 'L\'image d\'un triangle par un demi-tour est un triangle superposable au triangle de départ.', v: true, f: 'figure' },
    { l: 'L\'image d\'un triangle par une symétrie axiale a des angles différents de ceux du triangle de départ.', v: false, f: 'figure', code: 'conservation_niee' },
    { l: 'Pour nommer l\'image d\'un segment, on utilise les images de ses deux extrémités.', v: true, f: 'nommer' },
    { l: 'L\'image d\'un segment garde toujours une extrémité en commun avec le segment de départ.', v: false, f: 'nommer', code: 'un_seul_point_transforme' },
];

let dernieresLignesRappel1121 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1121 = [];
        if (lignes.some((it) => dernieresLignesRappel1121.includes(it.l))) continue;
        dernieresLignesRappel1121 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les images par une symétrie axiale ou un demi-tour, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1121 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    point_qcm_unique: genPoint,
    segment_qcm_unique: genSegment,
    figure_qcm_unique: genFigure,
    points_relier: genRelier,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', relier: 'Relier', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'point', label: L_POINT, formatsList: buildFormats('point') },
    { id: 'segment', label: L_SEGMENT, formatsList: buildFormats('segment') },
    { id: 'figure', label: L_FIGURE, formatsList: buildFormats('figure') },
    { id: 'points', label: L_RELIER, formatsList: buildFormats('points') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    point_qcm_unique: ['demi_tour_pour_axiale', 'autre_axe_de_la_figure'],
    segment_qcm_unique: ['demi_tour_pour_axiale', 'autre_axe_de_la_figure'],
    figure_qcm_unique: ['autre_axe_de_la_figure'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { CONFIGS, imageDe, imageSeg, imageDroite, imageTri, pointsFixes, tirerTransfo, dessiner, POOL_RAPPEL };
